// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button, Collapse, Form } from "react-bootstrap";
import { NavigateFunction } from "react-router-dom";
import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import { isSet } from "shared/utils/utils";
import { IAction, ActionType, raiseAction, IActionTypeChangeUser, IActionSortTable } from "actions";
import { UserAccessRights } from "shared/types/cloudtypes";
import { DataState, MainPanel } from "types";
import { IState } from "states/states";
import { ITableSort, UserSettingsArea } from "states/guistates";
import { Company, Site, User } from "states/sitestates";
import {
  isStateDirty,
  makeSortHeader,
  toDropdownItems,
  getAccessibleSiteIds,
  setSelectedIds,
} from "guiutils";

import AlarmNotifications from "view/comp/AlarmNotifications";
import SaveFeedback from "view/comp/SaveFeedback";
import Dropdown from "view/comp/Dropdown";
import SearchFilter from "view/comp/SearchFilter";
import SelectAllCheck from "view/comp/SelectAllCheck";
import SearchableDropdown from "view/comp/SearchableDropdown";
import IconExpandMore from "icons/iconExpandMore";
import IconExpandLess from "icons/iconExpandLess";

// sort key of table
const TABLE_NAME = "t_user";
const EMPTY_ACCESS_RIGHTS: UserAccessRights = {};
const EMPTY_COMPANIES: Company[] = [];

interface IEnlineUserAdminProps {
  loginAccessRights: UserAccessRights;
  loginCompanyId: number | undefined;
  loginUserId: number | undefined;
  companyList: Company[];
  userList: User[];
  showCompanyPrefix: boolean;
  user: User | undefined;
  isUserLoaded: boolean;
  allSites: Site[];
  userSites: number[];
  dataState: DataState;
  tableSort: ITableSort | undefined;
  saveError: string | undefined;
  openedUserSettingsArea: UserSettingsArea[] | undefined;
}

const mapStateToProps = (state: IState) => {
  // logged-in user
  const loginUser = state.site.userList?.find(
    (curUser) => curUser.secId === state.site.user?.secId,
  );
  // user to edit, by default the logged-in user
  const user =
    state.site.userList?.find((curUser) => state.router.user === curUser.id) ?? loginUser;

  const props: IEnlineUserAdminProps = {
    loginAccessRights: loginUser?.accessRights ?? EMPTY_ACCESS_RIGHTS,
    loginCompanyId: loginUser?.companyId,
    loginUserId: loginUser?.id,
    companyList: state.site.companyList ?? EMPTY_COMPANIES,
    userList: state.site.userList ?? [],
    showCompanyPrefix: (state.site.companyList?.length ?? 0) > 1,
    user,
    isUserLoaded: isSet(state.scheduler.userListExt.lastData),
    allSites: state.site.siteList ?? [],
    userSites: user?.siteIds ?? [],
    dataState: state.scheduler.saveState,
    tableSort: state.gui.tableSort[TABLE_NAME],
    saveError: state.gui.login.userErrorText,
    openedUserSettingsArea: state.gui.openedUserSettingsArea,
  };
  return props;
};

interface IEnlineUserAdminState {
  // a new user is created instead of modifying the selected one
  isCreating: boolean;
  // a user other than the logged-in one is administrated
  selectForeignUser: boolean;
  userId: number | undefined;
  wasUserLoaded: boolean;
  name: string | undefined;
  email: string | undefined;
  companyId: number | undefined;
  userSites: number[];
  sortUserSites: number[]; // snapshot used for sorting, so rows don't jump when toggled
  accessRights: UserAccessRights;
  filterText: string;
}

interface IEnlineUserAdminDispatch {
  onSort: (sort: IActionSortTable) => void;
  onSelectUser: (userId: number, navigate: NavigateFunction) => void;
  onCreateUser: () => void;
  onAreaToggle: (areas: UserSettingsArea[] | undefined, area: UserSettingsArea) => void;
  onCancelClick: (userId: number | undefined, navigate: NavigateFunction) => void;
  onOkClick: (id: number | undefined, state: IEnlineUserAdminState) => void;
  onChanges: (dataState: DataState) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IEnlineUserAdminDispatch = {
    onSort: (sort: IActionSortTable) => {
      dispatch(raiseAction(ActionType.ACT_SORT_TABLE, sort));
    },
    onSelectUser: (userId: number, navigate: NavigateFunction) => {
      navigate(`../${MainPanel.USER_SETTINGS}?user=${userId}`, {
        state: { resetUnsavedChanges: true },
      });
    },
    onCreateUser: () => {
      // clear a pending signup error of a previous creation
      dispatch(raiseAction(ActionType.ACT_SIGNUP));
    },
    onAreaToggle: (areas: UserSettingsArea[] | undefined, area: UserSettingsArea) => {
      areas =
        !areas ? [area]
        : areas.includes(area) ? areas.filter((curArea) => curArea !== area)
        : [...areas, area];
      dispatch(raiseAction(ActionType.ACT_USER_SETTINGS_AREA_TOGGLE, areas));
    },
    onCancelClick: (userId: number | undefined, navigate: NavigateFunction) => {
      // change location, which will also clear dirty flag and close the modal
      navigate(`../${MainPanel.USER_SETTINGS}${isSet(userId) ? `?user=${userId}` : ""}`, {
        state: { resetUnsavedChanges: true },
      });
    },
    onOkClick: (id: number | undefined, state: IEnlineUserAdminState) => {
      const payload: IActionTypeChangeUser = {
        id,
        email: state.email,
        name: state.name,
        companyId: state.companyId,
        userSites: state.userSites,
        accessRights: state.accessRights,
      };

      dispatch(raiseAction(ActionType.ACT_CHANGE_USER_REQ, payload));
    },
    onChanges: (dataState: DataState) => {
      if (!isStateDirty(dataState)) {
        dispatch(raiseAction(ActionType.ACT_SET_PAGE_DIRTY, true));
      }
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  selectCompany: {
    id: "Select company",
  },
  selectUser: {
    id: "Select user",
  },
  ownUser: {
    id: "Own user",
  },
  foreignUser: {
    id: "Other user",
  },
  newUser: {
    id: "new user",
  },
  placeholderSearchFiled: {
    id: "Search for sites...",
  },
  // access rights messages
  ackAlarms: {
    id: "Acknowledge Alarms",
  },
  modifyAlarms: {
    id: "Alarms",
  },
  modifyAlarmsSecondary: {
    id: "activate/edit alarms",
  },
  importTrendData: {
    id: "Import Trend data",
  },
  setVirtualInputs: {
    id: "Set Virtual Inputs",
  },
  switchOutputs: {
    id: "Control Outputs",
  },
  setParameter: {
    id: "Update Parameters",
  },
  ocppSettings: {
    id: "OCPP Change Settings",
  },
  ocppSettingsSecondary: {
    id: "RFIDs, tariffs, chargers public/private",
  },
  ocppChargerAdmin: {
    id: "OCPP Administrate Chargers",
  },
  ocppChargerAdminSecondary: {
    id: "delete charger, change connector names",
  },
  ocppChargerCtrl: {
    id: "OCPP Control Chargers",
  },
  ocppChargerCtrlSecondary: {
    id: "start/stop transactions, unlock connector, reboot charger",
  },
  showEnlineChargers: {
    id: "Energy Management Chargers",
  },
  showEnlineChargersSecondary: {
    id: "access energy management charger data",
  },
  controllerAdmin: {
    id: "Local Controller Admin",
  },
  controllerAdminSecondary: {
    id: "show extended controller data (modules, conditions) and allow reboot",
  },
  createReports: {
    id: "Reports",
  },
  createReportsSecondary: {
    id: "create/edit/delete reports",
  },
  crudTrendMonitors: {
    id: "Trend Monitors",
  },
  crudTrendMonitorsSecondary: {
    id: "create/edit/delete Trend Monitors",
  },
  setVirtualParameters: {
    id: "View and edit parameters of virtual sites",
  },
  siteAccess: {
    id: "Access all sites belonging to company and subsidiaries",
  },
  modifyUsers: {
    id: "Modify all users belonging to company and subsidiaries",
  },
  modifySites: {
    id: "Modify all sites belonging to company and subsidiaries",
  },
});

// access rights editable via checkbox; openSshTunnel holds a port and is set in the database only
type BooleanAccessRight = Exclude<keyof UserAccessRights, "openSshTunnel">;

// define access rights configuration
const accessRightsConfig: Array<{
  key: BooleanAccessRight;
  message: { id: string };
  secondaryMessage?: { id: string };
}> = [
  { key: "ackAlarms", message: messages.ackAlarms },
  {
    key: "modifyAlarms",
    message: messages.modifyAlarms,
    secondaryMessage: messages.modifyAlarmsSecondary,
  },
  { key: "importTrendData", message: messages.importTrendData },
  { key: "setVirtualInputs", message: messages.setVirtualInputs },
  { key: "switchOutputs", message: messages.switchOutputs },
  { key: "setParameter", message: messages.setParameter },
  {
    key: "ocppSettings",
    message: messages.ocppSettings,
    secondaryMessage: messages.ocppSettingsSecondary,
  },
  {
    key: "ocppChargerAdmin",
    message: messages.ocppChargerAdmin,
    secondaryMessage: messages.ocppChargerAdminSecondary,
  },
  {
    key: "ocppChargerCtrl",
    message: messages.ocppChargerCtrl,
    secondaryMessage: messages.ocppChargerCtrlSecondary,
  },
  {
    key: "showEnlineChargers",
    message: messages.showEnlineChargers,
    secondaryMessage: messages.showEnlineChargersSecondary,
  },
  {
    key: "controllerAdmin",
    message: messages.controllerAdmin,
    secondaryMessage: messages.controllerAdminSecondary,
  },
  {
    key: "createReports",
    message: messages.createReports,
    secondaryMessage: messages.createReportsSecondary,
  },
  {
    key: "crudTrendMonitors",
    message: messages.crudTrendMonitors,
    secondaryMessage: messages.crudTrendMonitorsSecondary,
  },
  { key: "setVirtualParameters", message: messages.setVirtualParameters },
  { key: "siteAccess", message: messages.siteAccess },
  { key: "modifySites", message: messages.modifySites },
  { key: "modifyUsers", message: messages.modifyUsers },
];

class UserAdminComponent extends React.Component<
  IEnlineUserAdminProps & IEnlineUserAdminDispatch & RoutedProps,
  IEnlineUserAdminState
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  state: IEnlineUserAdminState = {
    isCreating: false,
    selectForeignUser: this.props.user?.id !== this.props.loginUserId,
    userId: this.props.user?.id,
    wasUserLoaded: this.props.isUserLoaded,
    email: this.props.user?.email,
    name: this.props.user?.name,
    companyId: this.props.user?.companyId,
    accessRights: this.props.user?.accessRights ?? {},
    userSites: [...this.props.userSites],
    sortUserSites: this.props.userSites,
    filterText: "",
  };

  // the user shown in the form, undefined while a new user is created
  private get editedUser() {
    return this.state.isCreating ? undefined : this.props.user;
  }

  public componentDidUpdate() {
    if (
      this.props.user?.id !== this.state.userId ||
      this.state.wasUserLoaded !== this.props.isUserLoaded
    ) {
      this.setState({
        isCreating: false,
        selectForeignUser: this.props.user?.id !== this.props.loginUserId,
        userId: this.props.user?.id,
        wasUserLoaded: this.props.isUserLoaded,
        name: this.props.user?.name,
        companyId: this.props.user?.companyId,
        accessRights: this.props.user?.accessRights ?? this.state?.accessRights,
        userSites: [...this.props.userSites],
        sortUserSites: this.props.userSites,
      });
    }
  }

  public render() {
    return (
      <div id="useradmin">
        <div className="savebox" hidden={!this.props.loginAccessRights.modifyUsers}>
          {this.renderSaveChanges()}
          <SaveFeedback
            className="mb-2 mt-2 ms-2"
            dataState={
              isStateDirty(this.props.dataState) && !isSet(this.props.saveError) ?
                DataState.DIRTY
              : this.props.dataState
            }
            lastError={
              isSet(this.props.saveError) ?
                [this.context.formatMessage({ id: this.props.saveError })]
              : undefined
            }
            hideDirty={!isSet(this.editedUser)}
          />
        </div>
        {this.props.loginAccessRights.modifyUsers && this.renderUserSelection()}
        {/* below the selection, to make clear which user the sections refer to */}
        <h1>
          <FormattedMessage id="User" />
          {": "}
          {this.state.isCreating ?
            this.context.formatMessage(messages.newUser)
          : this.getUserLabel(this.editedUser)}
        </h1>
        {this.props.loginAccessRights.modifyUsers && this.renderUserEdit()}
        {isSet(this.editedUser) && (
          <AlarmNotifications userId={this.editedUser.id} userEmail={this.editedUser.email} />
        )}
      </div>
    );
  }

  private renderUserEdit() {
    return (
      <>
        {this.renderSectionHeader(
          UserSettingsArea.USER,
          <FormattedMessage id="User Administration" />,
        )}
        <Collapse in={this.isAreaExpanded(UserSettingsArea.USER)}>
          <div>{this.renderUserTable()}</div>
        </Collapse>
        {this.renderSectionHeader(UserSettingsArea.SITES, <FormattedMessage id="Site Access" />)}
        <Collapse in={this.isAreaExpanded(UserSettingsArea.SITES)}>
          <div>
            <div className="tableSearchHeader">
              <SearchFilter
                className="tableSearchBox"
                filterText={this.state.filterText}
                placeholder={this.context.formatMessage(messages.placeholderSearchFiled)}
                onChange={(filterText) => this.setState({ filterText })}
              />
            </div>
            {this.props.isUserLoaded ?
              this.renderSiteTable()
            : <FormattedMessage id="Loading..." />}
          </div>
        </Collapse>
      </>
    );
  }

  private renderUserSelection() {
    return (
      <div className="mb-3">
        <Form.Check
          type="radio"
          name="userSelection"
          id="ownUser"
          checked={!this.state.selectForeignUser}
          onChange={() => {
            this.setState({ selectForeignUser: false, isCreating: false });
            this.props.onSelectUser(this.props.loginUserId!, this.props.navigate);
          }}
          label={this.context.formatMessage(messages.ownUser)}
        />
        <Form.Check
          type="radio"
          name="userSelection"
          id="foreignUser"
          checked={this.state.selectForeignUser}
          onChange={() => this.setState({ selectForeignUser: true })}
          label={this.context.formatMessage(messages.foreignUser)}
        />
        {this.state.selectForeignUser && this.renderForeignUserSelection()}
      </div>
    );
  }

  private renderForeignUserSelection() {
    const userIds = this.props.userList
      .map((_curUser, curIndex) => curIndex)
      .sort((a, b) => this.props.userList[a].name.localeCompare(this.props.userList[b].name));

    return (
      <div className="buttonbar mt-2">
        <SearchableDropdown
          caption={this.context.formatMessage(messages.selectUser)}
          className="me-2"
          valueList={userIds}
          mapperFunction={(value: number | undefined) => {
            if (isSet(value)) {
              this.props.onSelectUser(this.props.userList[value].id, this.props.navigate);
            }
          }}
          labelFunction={(index: number) => this.getUserLabel(this.props.userList[index])}
          curItem={
            this.state.isCreating ?
              this.context.formatMessage(messages.newUser)
            : this.getUserLabel(this.editedUser) || this.context.formatMessage(messages.selectUser)
          }
          nextElementId="createUser"
        />
        <Button
          variant="outline-success"
          id="createUser"
          onClick={() => {
            this.props.onCreateUser();
            this.setState({
              isCreating: true,
              name: "",
              email: "",
              companyId: this.props.loginCompanyId,
              accessRights: {},
              userSites: [],
              sortUserSites: [],
            });
          }}
        >
          <FormattedMessage id="Create New" />
        </Button>
      </div>
    );
  }

  private getUserLabel(user: User | undefined) {
    if (!user) return "";
    const companyName = this.props.companyList.find((c) => c.id === user.companyId)?.name;
    return this.props.showCompanyPrefix && companyName ?
        `${companyName} | ${user.name}`
      : user.name;
  }

  private isAreaExpanded(area: UserSettingsArea) {
    return this.props.openedUserSettingsArea?.includes(area) ?? false;
  }

  private renderSectionHeader(area: UserSettingsArea, caption: JSX.Element) {
    return (
      <h1>
        <span
          className="link"
          onClick={() => this.props.onAreaToggle(this.props.openedUserSettingsArea, area)}
        >
          <Button variant="link" className="expand-button me-2 d-print-none">
            {this.isAreaExpanded(area) ?
              <IconExpandMore />
            : <IconExpandLess />}
          </Button>
          {caption}
        </span>
      </h1>
    );
  }

  private renderSaveChanges() {
    return (
      <div className="mb-2 mt-2">
        {isStateDirty(this.props.dataState) &&
          (isSet(this.editedUser) || this.isValidEmail(this.state.email)) &&
          isSet(this.state.companyId) && (
            <Button
              variant="primary"
              id="saveUserAdmin"
              type="submit"
              className={`me-2 ${!isSet(this.editedUser) ? "create_user" : ""}`}
              onClick={(_ev) => {
                this.props.onOkClick(this.editedUser?.id, this.state);
              }}
            >
              {!isSet(this.editedUser) ?
                <FormattedMessage id="Create User" />
              : <FormattedMessage id="Save Changes" />}
            </Button>
          )}
        {isStateDirty(this.props.dataState) && (
          <Button
            variant="secondary"
            id="cancelUserAdmin"
            type="submit"
            onClick={(_ev) => {
              // discard the entered data and reload the state of the shown user
              this.setState({
                isCreating: false,
                name: this.props.user?.name,
                email: this.props.user?.email,
                companyId: this.props.user?.companyId,
                accessRights: this.props.user?.accessRights ?? {},
                userSites: [...this.props.userSites],
                sortUserSites: this.props.userSites,
              });
              this.props.onCancelClick(this.props.user?.id, this.props.navigate);
            }}
          >
            <FormattedMessage id="Cancel" />
          </Button>
        )}
      </div>
    );
  }

  private renderUserTable() {
    const isEditMode = true;

    const companyIds = [...this.props.companyList]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => c.id);

    return (
      <table className="fullwidthwhite">
        <tbody>
          {isEditMode && !isSet(this.editedUser) && (
            <tr>
              <td>
                <FormattedMessage id="Email" />:
              </td>
              <td colSpan={2}>
                {isEditMode ?
                  <input
                    type="text"
                    value={this.state.email ?? ""}
                    disabled={this.props.dataState === DataState.SAVE_REQ}
                    onChange={(event) => {
                      this.setState({ email: event?.currentTarget.value });
                      this.props.onChanges(this.props.dataState);
                    }}
                  />
                : this.state.email}
              </td>
            </tr>
          )}
          <tr>
            <td>
              <FormattedMessage id="User name" />:
            </td>
            <td colSpan={2}>
              {isEditMode ?
                <input
                  type="text"
                  value={this.state.name ?? ""}
                  disabled={this.props.dataState === DataState.SAVE_REQ}
                  onChange={(event) => {
                    this.setState({ name: event?.currentTarget.value });
                    this.props.onChanges(this.props.dataState);
                  }}
                />
              : this.state.name}
            </td>
          </tr>
          <tr>
            {isSet(this.editedUser) && (
              <>
                <td>
                  <FormattedMessage id="Email Address" />:
                </td>
                <td colSpan={2}>{this.editedUser?.email}</td>
              </>
            )}
          </tr>
          {this.props.loginAccessRights.modifyUsers && (
            <tr>
              <td>
                <FormattedMessage id="Company" />:
              </td>
              <td colSpan={2}>
                {isEditMode ?
                  <Dropdown
                    className="btn-group-sm"
                    items={toDropdownItems(
                      companyIds,
                      "company",
                      (_name, companyId) => {
                        this.setState({ companyId: companyId });
                        this.props.onChanges(this.props.dataState);
                      },
                      (companyId) =>
                        this.props.companyList.find((c) => c.id === companyId)?.name ??
                        String(companyId),
                    )}
                    caption={this.context.formatMessage(messages.selectCompany)}
                    curItem={
                      this.props.companyList.find((c) => c.id === this.state.companyId)?.name ?? ""
                    }
                    disabled={this.props.dataState === DataState.SAVE_REQ}
                  />
                : this.props.companyList.find((c) => c.id === this.state.companyId)?.name}
              </td>
            </tr>
          )}
          <tr>
            <td rowSpan={accessRightsConfig.length + 1} style={{ verticalAlign: "top" }}>
              <FormattedMessage id="Special access rights" />:
            </td>
          </tr>
          {accessRightsConfig.map((config) =>
            this.renderAccessRightRow(
              config.key,
              config.message,
              config.secondaryMessage,
              isEditMode,
            ),
          )}
        </tbody>
      </table>
    );
  }

  private renderAccessRightRow(
    accessRight: BooleanAccessRight,
    message: { id: string },
    secondaryMessage?: { id: string },
    isEditMode = true,
  ) {
    return (
      <tr key={accessRight} className="access-right-row">
        <td>
          <Form.Label htmlFor={accessRight} className="link">
            <FormattedMessage {...message} />
            {secondaryMessage && (
              <>
                : <FormattedMessage {...secondaryMessage} />
              </>
            )}
          </Form.Label>
        </td>
        {this.renderCheckBoxSettingsData(accessRight, isEditMode)}
      </tr>
    );
  }

  private renderCheckBoxSettingsData(accessRight: BooleanAccessRight, isEditMode: boolean) {
    return (
      <td>
        {isEditMode ?
          <Form.Check
            name={accessRight}
            id={accessRight}
            checked={this.state.accessRights[accessRight] ? true : false}
            onChange={(event) => {
              const accessRights = { ...this.state.accessRights };
              accessRights[accessRight] = event.currentTarget.checked;
              this.setState({
                accessRights,
              });
              this.props.onChanges(this.props.dataState);
            }}
          />
        : this.editedUser?.accessRights[accessRight] ?
          <FormattedMessage id="yes" />
        : <FormattedMessage id="no" />}
      </td>
    );
  }

  // this function is just a short-cut to reduce boilerplate
  private makeSortHeader(column: keyof Site, caption: JSX.Element, classes?: string[]) {
    return makeSortHeader(
      TABLE_NAME,
      column,
      caption,
      this.props.tableSort,
      (sort) => {
        this.setState({ sortUserSites: this.state.userSites });
        this.props.onSort(sort);
      },
      classes,
    );
  }

  private setUserSites(siteIds: number[], isEnabled: boolean) {
    this.setState({ userSites: setSelectedIds(this.state.userSites, siteIds, isEnabled) });
    this.props.onChanges(this.props.dataState);
  }

  private renderSiteTable() {
    const isEditMode = true;

    // Calculate sites accessible via company hierarchy (when siteAccess is true)
    const companyId = this.state.companyId;
    const hierarchyAccessibleSites = new Set(
      isSet(companyId) ?
        getAccessibleSiteIds(
          { companyId, accessRights: this.state.accessRights },
          this.props.allSites,
          this.props.companyList,
        )
      : [],
    );

    const sortCol = (this.props.tableSort?.column ?? "name") as keyof Site;
    const filterTokens = this.state.filterText
      .toLocaleLowerCase()
      .split(/\s+/)
      .filter((token) => token.length > 0);
    const shownSites = [...this.props.allSites]
      .filter((site) => {
        if (filterTokens.length === 0) {
          return true;
        }
        const siteName = site.name.toLocaleLowerCase();
        const siteSerial = site.serial.toLocaleLowerCase();
        const siteCompanyName =
          this.props.companyList.find((c) => c.id === site.companyId)?.name?.toLocaleLowerCase() ??
          "";
        return filterTokens.every(
          (token) =>
            siteName.includes(token) ||
            siteSerial.includes(token) ||
            siteCompanyName.includes(token),
        );
      })
      .sort((a, b) => {
        let keyA =
          sortCol === "cloudSettings" ? !this.state.sortUserSites.includes(a.id) : a[sortCol];
        let keyB =
          sortCol === "cloudSettings" ? !this.state.sortUserSites.includes(b.id) : b[sortCol];
        if (typeof keyA === "string" && typeof keyB === "string") {
          keyA = keyA.toLocaleLowerCase();
          keyB = keyB.toLocaleLowerCase();
        }
        const sort =
          isSet(keyA) && isSet(keyB) ?
            keyA === keyB ? a.name.localeCompare(b.name)
            : keyA > keyB ? 1
            : -1
          : isSet(keyB) ? 1
          : -1;
        return this.props.tableSort?.isSortAscending !== false ? sort : -sort;
      });
    // sites with access via company hierarchy cannot be changed
    const editableSiteIds = shownSites
      .map((site) => site.id)
      .filter((id) => !hierarchyAccessibleSites.has(id));
    const siteItems = shownSites.map((curSite) => {
      const hasHierarchyAccess = hierarchyAccessibleSites.has(curSite.id);
      const hasExplicitAccess = this.state.userSites.includes(curSite.id);
      return (
        <tr key={curSite.id}>
          <td>
            <Form.Label htmlFor={`siteEnabled_${curSite.id}`} className="link">
              {curSite.name}
            </Form.Label>
          </td>
          <td className="nowrap">{curSite.serial}</td>
          <td>
            <Form.Label htmlFor={`siteEnabled_${curSite.id}`} className="link">
              {this.props.companyList.find((c) => c.id === curSite.companyId)?.name}
            </Form.Label>
          </td>
          <td className="center checkbox">
            {isEditMode ?
              <Form.Check
                name="siteEnabled"
                id={`siteEnabled_${curSite.id}`}
                checked={hasHierarchyAccess || hasExplicitAccess}
                disabled={hasHierarchyAccess}
                onChange={(event) => this.setUserSites([curSite.id], event.currentTarget.checked)}
              />
            : hasHierarchyAccess || hasExplicitAccess ?
              <FormattedMessage id="yes" />
            : <FormattedMessage id="no" />}
          </td>
        </tr>
      );
    });

    return (
      <table className="datatable fullwidthwhite scrolltable">
        <thead>
          <tr>
            {this.makeSortHeader("name", <FormattedMessage id="Site Name" />)}
            {this.makeSortHeader("serial", <FormattedMessage id="Serial" />, ["nowrap"])}
            {this.makeSortHeader("companyId", <FormattedMessage id="Company" />)}
            {this.makeSortHeader(
              "cloudSettings",
              <>
                {isEditMode && (
                  <SelectAllCheck
                    id="siteEnabledAll"
                    checkedCount={
                      editableSiteIds.filter((id) => this.state.userSites.includes(id)).length
                    }
                    totalCount={editableSiteIds.length}
                    onChange={(isEnabled) => this.setUserSites(editableSiteIds, isEnabled)}
                  />
                )}
                <FormattedMessage id="Access" />
              </>,
              ["center", "checkbox"],
            )}
          </tr>
        </thead>
        <tbody className="scrollbox">{siteItems}</tbody>
      </table>
    );
  }

  private isValidEmail(email: string | undefined): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = isSet(email) ? email.trim() : email;

    const isValid = isSet(trimmedEmail) && emailRegex.test(trimmedEmail);
    return isValid;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(UserAdminComponent));
